import React, {useState, useEffect} from "react";
import { useSelector } from "react-redux";
import {
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import UsersTable from "../../../components/common/Tables/UsersTable";
import RootState from "../../../redux/states/root.state";
import { useSearchParams } from "react-router-dom";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { CloseIcon, FilterLineIcon, UserCogIcon } from "../../../components/common/Icons/Icons";

interface ContainerProps {
  checked: any[];
  setChecked: any;
  setEditOpen: any;
  setDeleteOpen: any;
  setApplyOpen: any;
  setUser: any;
  setRole: any;
  role:any;
}

// URL params the filter panel owns (counted on the Filters button)
const filterParams = ['username', 'email', 'name', 'phone', 'role'];

const UsersContainer: React.FC<ContainerProps> = ({
  checked,
  setChecked,
  setEditOpen,
  setDeleteOpen,
  setApplyOpen,
  setUser,
  setRole,
  role
}) => {
  // Data
  const {data:roles,loading} = useSelector((state: RootState) => state.roles);
  const [searchParams, setSearchParams] = useSearchParams();

  const canAssignDeveloper = usePermissionCheck(['assign_developer_role']).canAccess;
  const canAssignAdmin = usePermissionCheck(['assign_admin_role']).canAccess;
  const canAssignCorporate = usePermissionCheck(['assign_corporate_role']).canAccess;
  const canAssignOwner = usePermissionCheck(['assign_owner_role']).canAccess;
  const canAssignDealer = usePermissionCheck(['assign_dealer_role']).canAccess;
  const canAssignStaff = usePermissionCheck(['assign_technician_role', 'assign_sales_role', 'assign_customer_role']).canAccess;
  const canAssignRoles = [canAssignDeveloper,canAssignAdmin,canAssignCorporate,canAssignOwner,canAssignDealer,canAssignStaff].includes(true);

  // Open by default: every filter in view
  const [showFilters, setShowFilters] = useState(true);

  // Search and Filter
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [userRole, setUserRole] = useState("");

  // Checked items
  const [isCheckAll, setCheckAll] = useState(false);


  useEffect(() => {
    setUsername(searchParams.get('username')??'')
    setEmail(searchParams.get('email')??'')
    setName(searchParams.get('name')??'')
    setPhone(searchParams.get('phone')??'')
    setUserRole(searchParams.get('role')??'')
  }, [searchParams]);

  const handleFilter = () => {
    searchParams.delete('page'); //reset page
    username==="" ? searchParams.delete('username') : searchParams.set('username', username);
    email==="" ? searchParams.delete('email') : searchParams.set('email', email);
    name==="" ? searchParams.delete('name') : searchParams.set('name', name);
    phone==="" ? searchParams.delete('phone') : searchParams.set('phone', phone);
    userRole==="" ? searchParams.delete('role') : searchParams.set('role', userRole);
    setChecked([]);
    setCheckAll(false);
    setSearchParams(searchParams);
  }

  const handleClear = () => {
    searchParams.delete('username');
    searchParams.delete('email');
    searchParams.delete('name');
    searchParams.delete('phone');
    searchParams.delete('role');
    searchParams.delete('page');
    setUsername('');
    setEmail('');
    setName('');
    setPhone('');
    setUserRole('');
    setChecked([]);
    setCheckAll(false);
    setSearchParams(searchParams);
  }

  const clearSelection = () => {
    setChecked([]);
    setCheckAll(false);
  }

  const sortRoles = () => {
    let new_array = [...roles];
    const sorted_array = ['developer','admin','corporate','owner','dealer','sales','technician','customer'];

    new_array.sort((a, b) => {
      return sorted_array.indexOf(a.name) - sorted_array.indexOf(b.name);
    });
    return new_array;
  }

  const activeFilters = filterParams.filter((param)=>searchParams.has(param)).length;

  const searchField = (id: string, label: string, value: string, setValue: (v: string) => void, autoComplete: string, className?: string) => (
    <TextField
      id={id}
      className={className}
      label={label}
      type="search"
      fullWidth={true}
      value={value??''}
      onChange={(e)=>setValue(e.target.value)}
      onKeyDown={(e)=>(e.code==="Enter" ? handleFilter() : null)}
      size="small"
      autoComplete={autoComplete}
    />
  );

  const toolbar = (
    <>
      <button
        type="button"
        className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? 'active' : ''}`}
        onClick={()=>setShowFilters(!showFilters)}
        aria-expanded={showFilters}
        aria-controls="lp-filters"
      >
        <FilterLineIcon/> Filters
        {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
      </button>

      {canAssignRoles && <>
        <span className="lp-divider" aria-hidden="true"/>

        {checked.length > 0 ? (
          <span className="lp-selection">
            {checked.length} selected
            <button type="button" className="lp-clear-selection" onClick={clearSelection} aria-label="Clear selection">
              <CloseIcon/>
            </button>
          </span>
        ) : (
          <span className="lp-hint">Select users to assign a role</span>
        )}

        <div className="lp-bulk">
          <FormControl size="small" className="lp-bulk-select">
            <InputLabel id="assign-role-select-label">Assign Role</InputLabel>
            <Select
              labelId="assign-role-select-label"
              id="assign-role-select"
              label="Assign Role"
              onChange={e => setRole(e.target.value)}
              value={role}
              disabled={checked.length===0 || loading}
            >
              {loading ? (
                <MenuItem value=""><CircularProgress size={"24px"} /></MenuItem>
              ) : (
                sortRoles().map((role: any) =>
                  ( ((role?.name === 'developer') && canAssignDeveloper)
                    || ((role?.name === 'admin') && canAssignAdmin)
                    || ((role?.name === 'corporate') && canAssignCorporate)
                    || ((role?.name === 'owner') && canAssignOwner)
                    || ((role?.name === 'dealer') && canAssignDealer)
                    || ((role?.name === 'sales' || role?.name === 'technician' || (role?.name === 'customer')) && canAssignStaff)
                  ) && (
                    <MenuItem key={role?.id} value={role?.id}>{role?.name.charAt(0).toUpperCase() + role?.name.slice(1)}</MenuItem>
                  )
                )
              )}
            </Select>
          </FormControl>

          <button
            type="button"
            className="btn btn-sm dt-tool-btn"
            disabled={checked.length===0}
            onClick={() => setApplyOpen(true)}
          >
            <UserCogIcon/> Apply Roles
            {checked.length>0 && <span className="lp-count">{checked.length}</span>}
          </button>
        </div>
      </>}
    </>
  );

  return (
    <div className={`lp-layout ${showFilters ? '' : 'filters-hidden'}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group lp-search">
              {searchField("name-search", "Name", name, setName, "user-name", "lp-wide")}
              {searchField("username-search", "Username", username, setUsername, "user-username", "lp-wide")}
              {searchField("email-search", "Email", email, setEmail, "user-email", "lp-wide")}
              {searchField("phone-search", "Phone", phone, setPhone, "user-phone", "lp-wide")}

              <FormControl fullWidth size="small" className="lp-wide">
                <InputLabel id="role-filter-label">Role</InputLabel>
                <Select
                  labelId="role-filter-label"
                  label="Role"
                  value={userRole??''}
                  onChange={e => setUserRole(e.target.value)}
                >
                  <MenuItem value={""}>All</MenuItem>
                  <MenuItem value={"developer"}>Developer</MenuItem>
                  <MenuItem value={"admin"}>Admin</MenuItem>
                  <MenuItem value={"corporate"}>Corporate</MenuItem>
                  <MenuItem value={"dealer"}>Dealer</MenuItem>
                  <MenuItem value={"owner"}>Owner</MenuItem>
                  <MenuItem value={"technician"}>Technician</MenuItem>
                  <MenuItem value={"sales"}>Sales</MenuItem>
                  <MenuItem value={"customer"}>Customer</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleFilter}>Apply Filters</button>
          </div>
        </aside>
      )}

      <UsersTable
        setEditOpen={setEditOpen}
        setDeleteOpen={setDeleteOpen}
        setUser={setUser}
        isChecked={checked}
        setChecked={setChecked}
        isCheckAll={isCheckAll}
        setCheckAll={setCheckAll}
        toolbar={toolbar}
      />
    </div>
  );
};

export default UsersContainer;
